import { useState } from "react";
import { ArrowsLeftRight, ArrowElbowDownRight, Trash, Camera, Calculator } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { SearchSelect } from "@/components/SearchSelect";
import CostMarginDialog from "@/components/CostMarginDialog";
import { money } from "@/lib/format";
import { api, formatApiErrorDetail } from "@/lib/api";

const COLS =
  "lg:grid-cols-[minmax(0,1.9fr)_minmax(0,1.7fr)_92px_48px_104px_150px_150px]";
const cell = "text-[10px] uppercase tracking-widest text-zinc-500 lg:hidden";

export const DeltaBadge = ({ delta, priced, testId }) => {
  if (!priced) return null;
  const d = Number(delta || 0);
  const same = Math.abs(d) < 0.005;
  const cls = same
    ? "border-zinc-600 text-zinc-400"
    : d < 0
      ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300"
      : "border-amber-400/40 bg-amber-500/10 text-amber-300";
  return (
    <span
      data-testid={testId}
      className={`whitespace-nowrap rounded-full border px-2 py-0.5 font-mono-data text-[10px] font-bold tabular-nums ${cls}`}
    >
      {same ? "Same price" : `${money(Math.abs(d))} ${d < 0 ? "less" : "more"}`}
    </span>
  );
};

export const MaterialOptionRow = ({
  quoteId,
  areaId,
  option,
  priced,
  materials,
  onEdit,
  onRemove,
  onUse,
}) => {
  const id = `${areaId}-${option.id}`;
  const p = priced || {};
  const [calcOpen, setCalcOpen] = useState(false);
  // a line-shaped object so the shared Cost & Margin dialog prices this colour option
  const optionLine = {
    item: option.slab_color || "Color option",
    kind: "material",
    qty: p.sqft ?? 0,
    unit: "SF",
    unit_price: option.unit_price,
    stone_group: option.stone_group || "",
    unit_cost: option.unit_cost,
    cost_total: option.cost_total,
    cost_basis: option.cost_basis,
    cost_mode: option.cost_mode,
    cost_pct: option.cost_pct,
    cost_supplier: option.cost_supplier,
    cost_tax_pct: option.cost_tax_pct,
    cost_other: option.cost_other,
    cost_width_in: option.cost_width_in,
    cost_height_in: option.cost_height_in,
  };
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !quoteId) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/areas/${areaId}/options/${option.id}/photo`, fd);
      onEdit(option.id, { photo_path: data.photo_path });
      toast.success("Photo attached to this color");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
  };
  const photoBtn = (
    <label
      data-testid={`material-option-photo-${id}`}
      title={option.photo_path ? "Replace the slab photo customers see for this color" : "Attach a slab photo so the customer card is never blank"}
      className={`cursor-pointer rounded border px-1.5 py-1 transition-colors duration-150 ${
        option.photo_path ? "border-emerald-500/40 text-emerald-300" : "border-zinc-700 text-zinc-500 hover:border-indigo-400/40 hover:text-indigo-300"
      } ${quoteId ? "" : "pointer-events-none opacity-40"}`}
    >
      {option.photo_path ? (
        <img
          alt=""
          src={`${process.env.REACT_APP_BACKEND_URL}/api/photos/${option.photo_path}?auth=${localStorage.getItem("sw_token")}&w=64`}
          className="h-[13px] w-[13px] rounded-sm object-cover"
        />
      ) : (
        <Camera size={13} weight="bold" />
      )}
      <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid={`material-option-photo-input-${id}`} />
    </label>
  );
  return (
    <>
    <div
      data-testid={`material-option-${id}`}
      className={`grid grid-cols-2 items-end gap-2 border-b border-dashed border-zinc-800/70 bg-zinc-950/40 px-2 py-2 lg:items-center ${COLS}`}
    >
      <div className="col-span-2 lg:col-span-1">
        <span className={cell}>Compare color</span>
        <div className="flex items-center gap-2 pl-3">
          <ArrowElbowDownRight size={14} className="shrink-0 text-amber-300/80" />
          <span className="w-[58px] shrink-0 truncate rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-amber-300">
            Option
          </span>
          <SearchSelect
            testId={`material-option-color-${id}`}
            value={option.slab_color || ""}
            options={materials || []}
            allowCustom
            placeholder="Pick a color to compare"
            searchPlaceholder="Colors, stock, remnants…"
            emptyText="Type a color name"
            onChange={(v) => onEdit(option.id, { pick: v })}
            className="h-9 px-3 text-sm font-semibold"
          />
        </div>
      </div>
      <div className="col-span-2 lg:col-span-1">
        <span className={cell}>Note</span>
        <div className="flex items-center gap-1.5">
          <Input
            data-testid={`material-option-note-${id}`}
            value={option.note || ""}
            onChange={(e) => onEdit(option.id, { note: e.target.value })}
            placeholder="Why this option (e.g. in stock, warmer tone)"
            className="h-9 border-zinc-800 bg-zinc-950 text-xs text-zinc-400"
          />
          <button
            type="button"
            data-testid={`material-option-recommend-${id}`}
            onClick={() => onEdit(option.id, { recommended: !option.recommended })}
            title={option.recommended ? "Recommended to the customer — click to clear" : "Mark as our recommendation"}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md border text-sm transition-colors duration-150 ${
              option.recommended
                ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-300"
                : "border-zinc-800 text-zinc-600 hover:border-emerald-500/50 hover:text-emerald-300"
            }`}
          >
            ★
          </button>
        </div>
      </div>
      <div>
        <span className={cell}>Qty</span>
        <div className="flex items-center justify-end gap-1">
          <Input
            data-testid={`material-option-qty-${id}`}
            type="number"
            step="0.1"
            value={option.qty_override ?? ""}
            placeholder={p.sqft != null ? String(p.sqft) : "auto"}
            onChange={(e) =>
              onEdit(option.id, {
                qty_override: e.target.value === "" ? null : e.target.value,
              })
            }
            className="h-9 w-[64px] border-zinc-800 bg-zinc-950 text-right font-mono-data text-xs"
          />
        </div>
        <span
          data-testid={`material-option-qty-hint-${id}`}
          className={`mt-0.5 block text-right text-[9px] uppercase tracking-wide ${
            option.qty_override == null || option.qty_override === ""
              ? "text-zinc-600"
              : "text-amber-400/80"
          }`}
        >
          {option.qty_override == null || option.qty_override === "" ? "from drawing" : "override"}
        </span>
      </div>
      <div className="text-xs text-zinc-500">
        <span className={cell}>Unit</span>SF
      </div>
      <div>
        <span className={cell}>Unit price</span>
        <div className="flex items-center gap-1">
          <Input
            data-testid={`material-option-price-${id}`}
            type="number"
            step="0.01"
            value={option.unit_price ?? ""}
            placeholder={p.priced ? String(p.unit_price ?? "") : "auto"}
            onChange={(e) =>
              onEdit(option.id, {
                unit_price: e.target.value === "" ? null : e.target.value,
              })
            }
            className="h-9 border-zinc-800 bg-zinc-950 text-right font-mono-data text-xs"
          />
          <button
            type="button"
            data-testid={`material-option-cost-calc-${id}`}
            onClick={() => setCalcOpen(true)}
            title="Cost & margin calculator for this colour"
            className={`flex h-9 w-8 shrink-0 items-center justify-center rounded-md border transition-colors duration-150 ${
              option.unit_cost
                ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-300"
                : "border-zinc-800 bg-zinc-950 text-zinc-500 hover:text-indigo-300"
            }`}
          >
            <Calculator size={13} weight="bold" />
          </button>
        </div>
        {option.unit_cost ? (
          <span
            data-testid={`material-option-cost-note-${id}`}
            className="mt-0.5 block text-right text-[9px] uppercase tracking-wide text-emerald-300/80"
          >
            cost {money(option.unit_cost)}
            {option.cost_pct != null
              ? ` · ${option.cost_mode || "markup"} ${option.cost_pct}%`
              : ""}
          </span>
        ) : (
          <span
            data-testid={`material-option-price-hint-${id}`}
            className={`mt-0.5 block text-right text-[9px] uppercase tracking-wide ${
              option.unit_price === null || option.unit_price === ""
                ? "text-zinc-600"
                : "text-amber-400/80"
            }`}
          >
            {option.unit_price === null || option.unit_price === ""
              ? p.priced
                ? `System ${money(p.unit_price)}/SF`
                : "Auto from system"
              : "Manual override"}
          </span>
        )}
      </div>
      <div className="min-w-0 text-right">
        <span className={cell}>Material if chosen</span>
        <div className="flex flex-col items-end gap-0.5 pr-1">
          <DeltaBadge
            delta={p.delta}
            priced={p.priced}
            testId={`material-option-delta-${id}`}
          />
          <div
            data-testid={`material-option-total-${id}`}
            className="font-mono-data whitespace-nowrap text-sm tabular-nums text-zinc-300"
          >
            {p.priced ? money(p.material) : "—"}
          </div>
        </div>
        <div className="whitespace-nowrap pr-1 text-[10px] text-zinc-500">
          {p.priced ? `Quote ${money(p.total)}` : "not charged"}
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-end gap-1.5">
        {photoBtn}
        <button
          data-testid={`material-option-use-${id}`}
          onClick={() => onUse(option.id)}
          disabled={!option.slab_color && (option.unit_price === null || option.unit_price === "")}
          title="Apply to the quote — the quoted material takes this color and your typed price"
          className="rounded border border-zinc-700 px-1.5 py-1 text-zinc-500 transition-colors duration-150 hover:border-emerald-400/40 hover:text-emerald-300 disabled:opacity-40"
        >
          <ArrowsLeftRight size={13} weight="bold" />
        </button>
        <button
          data-testid={`material-option-remove-${id}`}
          onClick={() => onRemove(option.id)}
          title="Remove this option"
          className="rounded border border-zinc-700 px-1.5 py-1 text-zinc-500 transition-colors duration-150 hover:border-red-500/40 hover:text-red-400"
        >
          <Trash size={13} weight="bold" />
        </button>
      </div>
    </div>
    <CostMarginDialog
      line={optionLine}
      open={calcOpen}
      onOpenChange={setCalcOpen}
      onApply={(patch) => onEdit(option.id, patch)}
    />
    </>
  );
};
